/* eslint-disable camelcase */
import moment from 'moment/moment';
import filesize from 'filesize';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { baseApi, parameterize } from './base.service';
import {
  AppsBehavior,
  ChangePermission,
  LayersBehavior,
  MembersBehavior,
  ProjectsBehavior,
  messagesGroup,
  messagesPermissions,
  transformGroup,
} from '../models/group';
import {
  BASE, defaults, isValidQueryLength, Params,
} from './__fixtures__/service-fixtures';
import type { GroupsResponse, IGroup as Group, Resource as GroupResource } from '../models/group';
import { setAvailable, updateTotalCount } from '../groups/menu-list.slice';
import { OptionItem } from '../components/cards/card.types';
import keysToCamelCase from '../utils/keys-to-camel-case';

export interface Resource {
  id: string;
  name: string;
  description: string;
}

interface GetGroupParams extends Params {
  associations: boolean;
}

export interface GetGroupsParams extends Partial<Params> {
  exclude_group_id: string[];
}

type ChangePermissionsParams = {
  search?: string;
  type: `${ChangePermission}`;
  groupId: string;
  add?: string[];
  addAll?: boolean;
  remove?: string[];
  item?: string;
  resource?: GroupResource;
  count?: number;
}

const groupsDefaults = (sort = 'name') => ({ ...defaults, sort });

function addMenuOptionsToGroups(groups: Group[]) {
  return groups.map((group) => ({
    ...group,
    options: [
      OptionItem.Edit,
      OptionItem.Delete,
    ],
  }));
}

export const groupsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getGroups: build.query<GroupsResponse<'groups'>, {}>({
      query: (options: Partial<GetGroupParams>) => {
        const params = { ...defaults, associations: true, ...options };
        return `${BASE}groups.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'groups'>) => {
        const groups = response.groups.map(keysToCamelCase);
        const groupsWithOptions = addMenuOptionsToGroups(groups);

        return { ...response, groups: groupsWithOptions };
      },
      providesTags: [{ type: 'Groups', id: 'LIST' }],
    }),
    getGroup: build.query<Group, Partial<GetGroupParams>>({
      query: ({ id, associations = true }) => `${BASE}groups/${id}.json?${parameterize({ associations })}`,
      transformResponse: (response: any) => transformGroup(response.group),
      providesTags: (group: any) => [{ type: 'Groups', id: group.resource_id } as const],
    }),
    createGroup: build.mutation<Group, Partial<Group>>({
      query({ name, description }) {
        return {
          url: `${BASE}groups`,
          method: 'POST',
          body: { name, description, is_managed: false },
        };
      },
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
      async onQueryStarted(_arg, { queryFulfilled }) {
        queryFulfilled.then(() => toast.success(messagesGroup.createGroup.success))
          .catch((error) => (error));
      },
    }),
    updateGroup: build.mutation<Group, Partial<Group>>({
      query(body) {
        return {
          url: `${BASE}groups/${body.id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
      async onQueryStarted(_arg, { queryFulfilled }) {
        queryFulfilled.then(() => toast.success(messagesGroup.updateGroup.success))
          .catch((error) => (error));
      },
    }),
    deleteGroup: build.mutation<Group, string>({
      query(id) {
        return {
          url: `${BASE}groups/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [{ type: 'Groups', id: 'LIST' }],
      async onQueryStarted(_arg, { queryFulfilled }) {
        toast.promise(queryFulfilled, messagesGroup.deleteGroup);
      },
    }),
    getGroupMembers: build.query<GroupsResponse<'members'>, Partial<Params>>({
      query: ({ id, ...options }) => {
        const params = { ...defaults, ...options };
        return `${BASE}groups/${id}/members.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'members'>) => {
        const memberships = response.memberships.map((member) => ({
          ...member,
          description: `${member.role_name}   |   ${member.email}`,
        }));

        return { memberships, total_pages: response.total_pages };
      },
      providesTags: [{ type: 'Groups', id: 'MEMBERS' }],
    }),
    getGroupsMembers: build.query<GroupsResponse<'members'>, GetGroupsParams>({
      query: ({ search, ...options }) => {
        const excludeSysUsers = { ...groupsDefaults('user'), exclude_system_users: '1' };
        const params = { ...excludeSysUsers, ...options };

        if (search && search.length >= 2) {
          params.search = search;
        }
        return `${BASE}memberships.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'members'>) => ({
        memberships: response.memberships,
        total_count: response.total_count,
        total_pages: response.total_pages,
      }),
      providesTags: [{ type: 'Members', id: 'GROUP_LIST' }],
      async onQueryStarted(_args, { queryFulfilled, dispatch }) {
        const memberBehavior = new MembersBehavior();
        const { data } = await queryFulfilled;
        const membersListItems = data.memberships.map(memberBehavior.mapResourceToListItem);
        dispatch(setAvailable(membersListItems));
        dispatch(updateTotalCount(data.total_count || 0));
      },
    }),
    getGroupApps: build.query<GroupsResponse<'forms'>, Partial<Params>>({
      query: ({ id, ...options }) => {
        const params = { ...defaults, ...options };
        return `${BASE}groups/${id}/forms.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'forms'>) => {
        const forms = response.forms.map(({
          id, name, image, record_changed_at, record_count,
        }) => ({
          id,
          name,
          image,
          description: record_changed_at
            ? `${record_count} records   |   Last activity ${moment(record_changed_at).fromNow()}`
            : 'No records collected.',
        }));

        return { forms, total_count: response.total_count, total_pages: response.total_pages };
      },
      providesTags: [{ type: 'Groups', id: 'APPS' }],
    }),
    getGroupsApps: build.query<GroupsResponse<'forms'>, GetGroupsParams>({
      query: ({ search, ...options }) => {
        let params = { ...groupsDefaults(), ...options };

        if (isValidQueryLength(search || '')) {
          params = { ...params, search };
        }

        return `${BASE}forms.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'forms'>) => ({
        forms: response.forms,
        total_count: response.total_count,
        total_pages: response.total_pages,
      }),
      providesTags: [{ type: 'Apps', id: 'GROUP_LIST' }],
      async onQueryStarted(_args, { queryFulfilled, dispatch }) {
        const appsBehavior = new AppsBehavior();
        const { data } = await queryFulfilled;
        const appsListItems = data.forms.map(appsBehavior.mapResourceToListItem);
        dispatch(setAvailable(appsListItems));
        dispatch(updateTotalCount(data.total_count || 0));
      },
    }),
    getGroupLayers: build.query<GroupsResponse<'layers'>, Partial<Params>>({
      query: ({ id, ...options }) => {
        const params = { ...defaults, ...options };
        return `${BASE}groups/${id}/layers.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'layers'>) => {
        const layers = response.layers.map((layer) => ({
          id: layer.id,
          name: layer.name,
          image: layer.image,
          description: layer.layer_type + (layer.file_size && layer.file_size > 0 ? `   |   ${filesize(layer.file_size, { round: 0 })}` : ''),
        }));

        return { layers, total_pages: response.total_pages };
      },
      providesTags: [{ type: 'Groups', id: 'LAYERS' }],
    }),
    getGroupsLayers: build.query<GroupsResponse<'layers'>, GetGroupsParams>({
      query: ({ search, ...options }) => {
        const params = { ...groupsDefaults(), ...options };

        if (search && search.length >= 2) {
          params.search = search;
        }
        return `${BASE}layers.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'layers'>) => ({
        layers: response.layers,
        total_count: response.total_count,
        total_pages: response.total_pages,
      }),
      providesTags: [{ type: 'Layers', id: 'GROUP_LIST' }],
      async onQueryStarted(_args, { queryFulfilled, dispatch }) {
        const layersBehavior = new LayersBehavior();
        const { data } = await queryFulfilled;
        const layersListItems = data.layers.map(layersBehavior.mapResourceToListItem);
        dispatch(setAvailable(layersListItems));
        dispatch(updateTotalCount(data.total_count || 0));
      },
    }),
    getGroupProjects: build.query<GroupsResponse<'projects'>, Partial<Params>>({
      query: ({ id, ...options }) => {
        const params = { ...defaults, ...options };
        return `${BASE}groups/${id}/projects.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'projects'>) => {
        const projects = response.projects.map((item: any) => ({
          id: item.id,
          name: item.name,
          image: item.image,
          description: `${item.record_count} records   |   ${item.form_count} apps using this project`,
        }));

        return { projects, total_pages: response.total_pages };
      },
      providesTags: [{ type: 'Groups', id: 'PROJECTS' }],
    }),
    getGroupsProjects: build.query<GroupsResponse<'projects'>, GetGroupsParams>({
      query: ({ search, ...options }) => {
        const params = { ...groupsDefaults(), ...options };

        if (search && search.length >= 2) {
          params.search = search;
        }
        return `${BASE}projects.json?${parameterize(params)}`;
      },
      transformResponse: (response: GroupsResponse<'projects'>) => ({
        projects: response.projects,
        total_count: response.total_count,
        total_pages: response.total_pages,
      }),
      providesTags: [{ type: 'Projects', id: 'GROUP_LIST' }],
      async onQueryStarted(_args, { queryFulfilled, dispatch }) {
        const projectsBehavior = new ProjectsBehavior();
        const { data } = await queryFulfilled;
        const projectsListItems = data.projects.map(projectsBehavior.mapResourceToListItem);
        dispatch(setAvailable(projectsListItems));
        dispatch(updateTotalCount(data.total_count || 0));
      },
    }),
    changePermissions: build.mutation<{}, ChangePermissionsParams>({
      query: ({
        type, groupId, add = [], remove = [], addAll = false, search,
      }) => ({
        url: `${BASE}groups/change_permissions.json`,
        method: 'POST',
        body: {
          change: {
            type, group_id: groupId, add, remove, add_all: addAll, search,
          },
        },
      }),
      invalidatesTags: [
        { type: 'Groups', id: 'MEMBERS' },
        { type: 'Groups', id: 'APPS' },
        { type: 'Groups', id: 'LAYERS' },
        { type: 'Groups', id: 'PROJECTS' },
        { type: 'Layers', id: 'GROUP_LIST' },
        { type: 'Members', id: 'GROUP_LIST' },
        { type: 'Apps', id: 'GROUP_LIST' },
        { type: 'Projects', id: 'GROUP_LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        if (arg.remove) {
          toast.promise(queryFulfilled, messagesPermissions(arg.item).delete);
        } else if (arg.count && (arg.count > 1)) {
          toast.promise(queryFulfilled, messagesPermissions(
            undefined, arg.resource, arg.count,
          ).create.many);
        } else {
          toast.promise(queryFulfilled, messagesPermissions(arg.item).create.one);
        }
      },
    }),

  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupMembersQuery,
  useGetGroupsMembersQuery,
  useGetGroupAppsQuery,
  useGetGroupsAppsQuery,
  useGetGroupLayersQuery,
  useGetGroupsLayersQuery,
  useGetGroupProjectsQuery,
  useGetGroupsProjectsQuery,
  useChangePermissionsMutation,
} = groupsApi;
